import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';

import { PainelLateral } from '@/shared/components/painel-lateral/painel-lateral';
import { CardContent } from '@/shared/components/card-content/card-content';
import { MedicoControleAdministrativoFeatureService } from '../mapa-brasil/medico-controle-administrativo.service';
import { MedicoCadastroGeralResponse } from '@/core/api/sicom-api/registro/medico/controle-administrativo/medico-cadastro-geral.model';

type OpcaoFiltro = { label: string; value: string | null };

@Component({
    selector: 'app-cadastro-geral',
    standalone: true,
    imports: [CommonModule, FormsModule, CardModule, SkeletonModule, SelectModule, ButtonModule, PainelLateral, CardContent],
    templateUrl: './cadastro-geral.html',
    styleUrl: './cadastro-geral.scss'
})
export class CadastroGeral implements OnInit {
    readonly inscricaoOpcoes: OpcaoFiltro[] = [
        { label: 'Principal', value: 'P' },
        { label: 'Secundária', value: 'S' }
    ];
    readonly generoOpcoes: OpcaoFiltro[] = [
        { label: 'Todos', value: null },
        { label: 'Masculino', value: 'M' },
        { label: 'Feminino', value: 'F' }
    ];
    readonly skeletons = [0, 1, 2, 3, 4, 5];

    inscricaoSelecionada: string | null = 'P';
    generoSelecionado: string | null = null;

    linhas: MedicoCadastroGeralResponse[] = [];
    carregando = false;

    constructor(
        private feature: MedicoControleAdministrativoFeatureService,
        private destroyRef: DestroyRef,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.aplicar();
    }

    aplicar(): void {
        this.carregando = true;
        this.feature
            .consultarCadastroGeral(this.inscricaoSelecionada ?? undefined, this.generoSelecionado ?? undefined)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                // markForCheck: os dados chegam em callback assíncrono e são gravados em campos
                // não-signal; sob o layout OnPush o tick da resposta não alcançaria este painel
                // (renderizava só após um clique). Marcar o caminho como "dirty" resolve.
                finalize(() => {
                    this.carregando = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (dados) => (this.linhas = dados ?? []),
                error: () => (this.linhas = [])
            });
    }

    percentual(parte: number, total: number): string {
        if (!total) return '0%';
        return `${Math.round((parte / total) * 100)}%`;
    }

    segmento(valor: number | undefined, total: number | undefined): number {
        return total ? ((valor ?? 0) / total) * 100 : 0;
    }

    get totais(): MedicoCadastroGeralResponse {
        return this.linhas.reduce(
            (acc, l) => ({
                ufSgUf: 'BR',
                inscritos: acc.inscritos + (l.inscritos ?? 0),
                ativos: acc.ativos + (l.ativos ?? 0),
                capital: acc.capital + (l.capital ?? 0),
                interior: acc.interior + (l.interior ?? 0),
                outrosEstados: acc.outrosEstados + (l.outrosEstados ?? 0),
                semEndereco: acc.semEndereco + (l.semEndereco ?? 0)
            }),
            { ufSgUf: 'BR', inscritos: 0, ativos: 0, capital: 0, interior: 0, outrosEstados: 0, semEndereco: 0 } as MedicoCadastroGeralResponse
        );
    }
}
